import { PDFTronZoomTools, ZoomLevelLimits, PDFTRON_DEFAULT_TOOL, PDFTronTools } from 'types';
import { WebViewerInstance, Core } from '@pdftron/webviewer';
import { getCachedFlagValue } from 'utils/getFlagValue/getFlagValue';

export enum PageWidthFit {
  fitPage = 'FitPage',
  fitWidth = 'FitWidth',
  resetZoom = 'ResetZoom',
}

interface AnnotationBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

class Zoom {
  instance: WebViewerInstance;
  previousZoomLevel: number = ZoomLevelLimits.defaultZoom * 100;

  constructor(instance: WebViewerInstance) {
    this.instance = instance;
  }

  getZoom() {
    return Math.floor(this.instance.UI.getZoomLevel() * 100);
  }

  // maybe this should be in a common mixin because we'll probably need it for other stuff?
  getIframeWindow() {
    return this.instance.UI.iframeWindow;
  }

  changeFitMode(fit: PageWidthFit) {
    const { FitWidth } = this.instance.UI.FitMode;
    if (fit === PageWidthFit.fitPage) {
      this.fitToPage();
    } else if (fit === PageWidthFit.fitWidth) {
      this.instance.UI.setFitMode(FitWidth);
    }
  }

  fitToPage() {
    const { FitPage } = this.instance.UI.FitMode;
    // This hack fixes the bug that fitPage zoom is not consistant during the first 2 calls
    this.instance.UI.setFitMode(FitPage);
    this.instance.UI.setFitMode(FitPage);
  }

  changeZoom(zoomLevel: number, zoomPosition?: { x: number; y: number }) {
    // get the scale and zoom factors
    const newZoomFactor = zoomLevel / 100;
    const currentZoomFactor = this.instance.UI.getZoomLevel();
    const scale = newZoomFactor / currentZoomFactor;

    // get the center coordinates
    const documentWrapper = this.instance.UI.iframeWindow.document.querySelectorAll('.document')[0];
    const documentContainer = this.instance.UI.iframeWindow.document.querySelectorAll('.DocumentContainer')[0];

    if (!documentContainer) return;

    const clientX = zoomPosition ? zoomPosition.x : documentContainer.clientWidth / 2;
    const clientY = zoomPosition ? zoomPosition.y : documentContainer.clientHeight / 2;
    const x =
      (clientX + documentContainer.scrollLeft - (documentWrapper as HTMLElement).offsetLeft) * scale -
      clientX +
      (documentContainer as HTMLElement).offsetLeft;
    const y =
      (clientY + documentContainer?.scrollTop - (documentWrapper as HTMLElement).offsetTop) * scale -
      clientY +
      (documentContainer as HTMLElement).offsetTop;

    // zoom to center
    this.instance.Core.documentViewer.zoomTo(newZoomFactor, x, y);
    this.setPreviousZoomLevel(zoomLevel);
  }

  toggleMarqueeTool(selectMarquee: boolean, previousTool?: PDFTronTools) {
    if (selectMarquee) {
      this.instance.UI.setToolMode(PDFTronZoomTools.MARQUEE);
    } else {
      this.instance.UI.setToolMode(previousTool || PDFTRON_DEFAULT_TOOL);
    }
  }

  private parseCustomBounds(
    customData: string | undefined | null,
    annotation: Core.Annotations.Annotation,
  ): AnnotationBounds | null {
    if (!customData) return null;
    try {
      const bounds = JSON.parse(customData);
      const rawX = (bounds.x ?? annotation.X) || 0;
      const rawY = (bounds.y ?? annotation.Y) || 0;
      const rawWidth = bounds.width || 0;
      const rawHeight = bounds.height || 0;

      // Normalize bounds: when width or height is negative (mirrored/rotated images),
      // adjust x/y to represent the actual top-left corner of the visible area
      return {
        x: rawWidth < 0 ? rawX + rawWidth : rawX,
        y: rawHeight < 0 ? rawY + rawHeight : rawY,
        width: Math.abs(rawWidth),
        height: Math.abs(rawHeight),
      };
    } catch (err) {
      console.error('Failed to parse annotation bounds metadata:', err);
      return null;
    }
  }

  getAnnotationBounds(annotation: Core.Annotations.Annotation): AnnotationBounds {
    if (getCachedFlagValue('reading_order_ux_v2')) {
      const readingOrderBounds = this.parseCustomBounds(
        annotation.getCustomData('readingOrderContextBounds'),
        annotation,
      );
      if (readingOrderBounds) return readingOrderBounds;
    }

    const originalBounds = this.parseCustomBounds(annotation.getCustomData('originalBounds'), annotation);
    if (originalBounds) return originalBounds;

    // Normalize annotation bounds for negative width/height (mirrored/rotated images)
    const rawWidth = annotation.Width || 0;
    const rawHeight = annotation.Height || 0;
    return {
      x: rawWidth < 0 ? (annotation.X || 0) + rawWidth : annotation.X || 0,
      y: rawHeight < 0 ? (annotation.Y || 0) + rawHeight : annotation.Y || 0,
      width: Math.abs(rawWidth),
      height: Math.abs(rawHeight),
    };
  }

  private calculateFitToPageZoom(pageNumber: number): number {
    const documentViewer = this.instance.Core.documentViewer;
    const viewElement = documentViewer.getScrollViewElement();
    const pageWidth = documentViewer.getPageWidth(pageNumber);
    const pageHeight = documentViewer.getPageHeight(pageNumber);
    const viewerWidth = viewElement.clientWidth;
    const viewerHeight = viewElement.clientHeight;

    // Calculate zoom to fit page
    const fitPageX = viewerWidth / pageWidth;
    const fitPageY = viewerHeight / pageHeight;
    return Math.min(fitPageX, fitPageY);
  }

  setDifferenceZoomLevel(
    annotation: Core.Annotations.Annotation,
    zoomLocked: boolean = false,
    options: {
      padding: number;
      upperBound: number;
    } = {
      padding: 200,
      upperBound: ZoomLevelLimits.differenceMax,
    },
  ): { effectivePadding: number; usedXAxis: boolean; usedFitToPage: boolean } | null {
    if (zoomLocked) {
      return null;
    }

    const documentViewer = this.instance?.Core.documentViewer;
    const viewElement = documentViewer.getScrollViewElement();

    if (!this.instance || !viewElement || !annotation) {
      return null;
    }

    const pageNumber = annotation.getPageNumber();
    const bounds = this.getAnnotationBounds(annotation);

    // Ensure we are on the correct page for consistent centering.
    // Guarded: re-setting the current page re-fires pageNumberUpdated/visiblePagesChanged and adds
    // render churn when a caller (e.g. the focus saga) has already navigated here.
    if (documentViewer.getCurrentPage() !== pageNumber) {
      documentViewer.setCurrentPage(pageNumber, false);
    }

    const viewerWidth = viewElement.clientWidth;
    const viewerHeight = viewElement.clientHeight;

    // Cap padding so the viewport still has positive inner width/height (otherwise fitX/fitY <= 0
    // and we keep the current zoom — common for tiny annotations + large dynamic padding + Color extra padding).
    const maxPad = Math.min(Math.max(0, (viewerWidth - 1) / 2), Math.max(0, (viewerHeight - 1) / 2));
    const padding = Math.min(options.padding, maxPad);

    // Compute a zoom that fully fits the annotation (with padding) inside the viewport
    const fitX = (viewerWidth - 2 * padding) / bounds.width;
    const fitY = (viewerHeight - 2 * padding) / bounds.height;
    let desiredZoom = Math.min(fitX, fitY);

    if (!isFinite(desiredZoom) || desiredZoom <= 0) {
      desiredZoom = documentViewer.getZoomLevel();
    }

    // Calculate fit-to-page zoom
    const fitToPageZoom = this.calculateFitToPageZoom(pageNumber);

    // Determine which axis was used (x or y)
    const usedXAxis = fitX <= fitY;

    // Respect limits for difference zooming
    const lowerBound = ZoomLevelLimits.minZoom;
    const clampedZoom = Math.min(options.upperBound, Math.max(lowerBound, desiredZoom));

    // Check if desired zoom would be zooming out further than fit-to-page
    const useFitToPage = desiredZoom < fitToPageZoom;

    if (useFitToPage) {
      // Use fit-to-page instead
      this.fitToPage();
      this.setPreviousZoomLevel(Math.floor(fitToPageZoom * 100));
      return { effectivePadding: padding, usedXAxis, usedFitToPage: true };
    } else {
      // Set zoom level
      this.instance.UI.setZoomLevel(clampedZoom);
      this.setPreviousZoomLevel(Math.floor(clampedZoom * 100));

      // Calculate the actual effective padding used
      const effectivePadding = usedXAxis
        ? (viewerWidth - bounds.width * clampedZoom) / 2
        : (viewerHeight - bounds.height * clampedZoom) / 2;

      return { effectivePadding, usedXAxis, usedFitToPage: false };
    }
  }

  centerOnAnnotation(annotation: Core.Annotations.Annotation) {
    const documentViewer = this.instance?.Core.documentViewer;
    const viewElement = documentViewer.getScrollViewElement();
    if (!this.instance || !viewElement || !annotation) return;

    const pageNum = annotation.getPageNumber();
    // Guarded: avoid redundant setCurrentPage churn when we're already on the page.
    if (documentViewer.getCurrentPage() !== pageNum) {
      documentViewer.setCurrentPage(pageNum, false);
    }

    const bounds = this.getAnnotationBounds(annotation);

    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    const dm = documentViewer.getDisplayModeManager().getDisplayMode();
    const windowPoint = dm.pageToWindow({ x: centerX, y: centerY }, pageNum);
    viewElement.scrollLeft = windowPoint.x - viewElement.clientWidth / 2;
    viewElement.scrollTop = windowPoint.y - viewElement.clientHeight / 2;
  }

  setMaxZoomLevel(zoomLevel: number) {
    // the number we pass in is multiplied by 100, so in order to limit to 9999% we need to pass 99.99
    this.instance.UI.setMaxZoomLevel(zoomLevel);
  }

  setMinZoomLevel(zoomLevel: number) {
    // the number we pass in is multiplied by 100, so in order to limit to 5% we need to pass 0.05
    this.instance.UI.setMinZoomLevel(zoomLevel);
  }

  getPreviousZoomLevel(): number {
    return this.previousZoomLevel;
  }

  setPreviousZoomLevel(zoomLevel: number): void {
    this.previousZoomLevel = zoomLevel;
  }
}

export default Zoom;
